import { useEffect, useState } from "react";
import { X, Upload, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const EMPTY_FORM = { customer_name: "", date_presented: "", created_by: "", created_for: "" };

/**
 * UploadRepositoryDocumentModal — upload a PDF/PPTX/DOCX into one repository category.
 */
export function UploadRepositoryDocumentModal({ open, onOpenChange, category, onCreated }) {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null);
      setForm({ ...EMPTY_FORM, created_by: user?.name || "" });
    }
  }, [open, user]);

  async function submit() {
    if (!file) { toast.error("Choose a file to upload"); return; }
    if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("customer_name", form.customer_name.trim());
      if (form.date_presented) fd.append("date_presented", form.date_presented);
      if (form.created_by.trim()) fd.append("created_by", form.created_by.trim());
      if (form.created_for.trim()) fd.append("created_for", form.created_for.trim());
      const { data } = await api.post("/repositories", fd);
      toast.success("Document uploaded");
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-[#12324A]/45 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => onOpenChange(false)}
      data-testid="upload-repository-modal"
    >
      <div
        className="w-full max-w-lg bg-[#F3FAFD] border border-[#BBD8E7] rounded-[26px] shadow-[0_24px_70px_rgba(7,83,154,0.22)] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[#C9DCE6] bg-[#E7F5FB]">
          <span className="w-9 h-9 rounded-xl bg-white border border-[#C9DCE6] text-[#07539A] flex items-center justify-center"><Upload size={15} /></span>
          <div className="font-display text-[18px] flex-1 text-[#12324A]">Upload document</div>
          <button onClick={() => onOpenChange(false)} data-testid="upload-repo-close" className="w-9 h-9 rounded-full bg-white border border-[#C9DCE6] text-[#607D8B] hover:text-[#07539A] hover:border-[#07539A] flex items-center justify-center">
            <X size={15} />
          </button>
        </div>

        <div className="p-5 space-y-3.5">
          <div className="rounded-2xl bg-white border border-[#C9DCE6] p-3">
            <label className="text-[11px] font-semibold text-[#455A64]">File (PDF, PPTX, or DOCX)</label>
            <input
              type="file"
              accept=".pdf,.pptx,.docx"
              onChange={e => setFile(e.target.files?.[0] || null)}
              data-testid="upload-repo-file"
              className="w-full mt-1 text-[12px] text-[#243B53] file:mr-3 file:h-9 file:px-3 file:rounded-lg file:border-0 file:bg-[#07539A] file:text-white file:text-[12px] file:font-medium"
            />
          </div>

          <div className="rounded-2xl bg-white border border-[#C9DCE6] p-3">
            <label className="text-[11px] font-semibold text-[#455A64]">Customer name *</label>
            <input
              value={form.customer_name}
              onChange={e => setForm({ ...form, customer_name: e.target.value })}
              placeholder="e.g. Vapi Steel Ltd"
              data-testid="upload-repo-customer"
              className="w-full h-10 mt-1 bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] rounded-xl px-3 text-[13px] text-[#243B53] outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-white border border-[#C9DCE6] p-3">
              <label className="text-[11px] font-semibold text-[#455A64]">Date presented</label>
              <input
                type="date"
                value={form.date_presented}
                onChange={e => setForm({ ...form, date_presented: e.target.value })}
                data-testid="upload-repo-date"
                className="w-full h-10 mt-1 bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] rounded-xl px-3 text-[13px] text-[#243B53] outline-none"
              />
            </div>
            <div className="rounded-2xl bg-white border border-[#C9DCE6] p-3">
              <label className="text-[11px] font-semibold text-[#455A64]">Created by</label>
              <input
                value={form.created_by}
                onChange={e => setForm({ ...form, created_by: e.target.value })}
                data-testid="upload-repo-created-by"
                className="w-full h-10 mt-1 bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] rounded-xl px-3 text-[13px] text-[#243B53] outline-none"
              />
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-[#C9DCE6] p-3">
            <label className="text-[11px] font-semibold text-[#455A64]">Created for</label>
            <input
              value={form.created_for}
              onChange={e => setForm({ ...form, created_for: e.target.value })}
              placeholder="e.g. client demo, partner event"
              data-testid="upload-repo-created-for"
              className="w-full h-10 mt-1 bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] rounded-xl px-3 text-[13px] text-[#243B53] outline-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#C9DCE6] bg-white/70">
          <button onClick={() => onOpenChange(false)} data-testid="upload-repo-cancel" className="h-10 px-4 rounded-xl text-[13px] text-[#607D8B] hover:text-[#07539A]">Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="upload-repo-submit"
            className="h-10 px-4 rounded-xl bg-[#07539A] hover:bg-[#063F73] !text-white text-[13px] font-medium flex items-center gap-2 disabled:opacity-50 shadow-[0_7px_18px_rgba(7,83,154,0.16)]"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            Upload
          </button>
        </div>
      </div>
    </div>
  );
}
